import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Delete } from "lucide-react";
import { endpoints, setAuth, getToken, clearAuth } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";

export default function RefLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const expired = params.get("expired") === "1";

  useEffect(() => {
    // Only auto-redirect if the stored token actually works. If it's stale,
    // clear it silently so the user can just enter their PIN.
    let cancelled = false;
    const t = getToken();
    if (!t) return () => { cancelled = true; };
    endpoints.auth_me()
      .then((me) => {
        if (cancelled) return;
        if (me?.role === "referee" || me?.role === "admin") {
          nav("/referee", { replace: true });
        }
      })
      .catch(() => { clearAuth(); });
    return () => { cancelled = true; };
  }, [nav]);

  const submit = async (finalPin) => {
    if (finalPin.length !== 4) return;
    setLoading(true);
    try {
      const res = await endpoints.refereeLogin(finalPin);
      setAuth(res);
      toast.success(`Welcome, ${res.name || "Referee"}`);
      nav("/referee", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const press = (d) => {
    setPin((prev) => {
      const next = (prev + d).slice(0, 4);
      if (next.length === 4) submit(next);
      return next;
    });
  };
  const back = () => setPin((p) => p.slice(0, -1));

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <div className="mx-auto flex w-full max-w-md items-center justify-between px-4 py-3">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="display text-sm font-bold uppercase tracking-[0.28em]">
            Referee Access
          </span>
        </Link>
        <ThemeToggle />
      </div>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 px-4 py-4">
        <div className="pt-2 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-sm bg-primary text-primary-foreground">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="8.5" />
              <path d="M12 3.5v17M3.5 12h17" />
            </svg>
          </div>
          <h1 className="mt-3 display text-2xl font-black uppercase tracking-tight">
            Enter PIN
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">4-digit referee code</p>

          {expired && (
            <div
              data-testid="session-expired-banner"
              className="mx-auto mt-3 max-w-xs rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-amber-500"
            >
              Session expired — please re-enter your PIN
            </div>
          )}

          <div className="mt-4 flex items-center justify-center gap-2.5" data-testid="pin-display">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-12 w-10 rounded-sm border-2 flex items-center justify-center display num-mono text-2xl font-black ${
                  pin[i]
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground"
                }`}
              >
                {pin[i] ? "•" : ""}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5 pb-2">
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <Key key={d} label={d} onClick={() => press(d)} testid={`pin-key-${d}`} disabled={loading} />
          ))}
          <div />
          <Key label="0" onClick={() => press("0")} testid="pin-key-0" disabled={loading} />
          <button
            onClick={back}
            data-testid="pin-key-back"
            disabled={loading}
            className="h-14 rounded-sm border border-border bg-card text-muted-foreground tap-feedback no-select flex items-center justify-center hover:text-destructive"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Key({ label, onClick, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="h-16 rounded-sm border border-border bg-card display text-3xl font-black tap-feedback no-select disabled:opacity-50 hover:border-primary/60"
    >
      {label}
    </button>
  );
}
